import { DailyEntry, FeelingEntry } from "../types/types"

// ---- Helpers ----
function pad2(n: number) {
  return String(n).padStart(2, "0")
}

function isoDayFromDate(d: Date) {
  // local date -> YYYY-MM-DD
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

function startOfDayLocal(dateIso: string) {
  // Handles "YYYY-MM-DDTHH:mm:ss" etc.
  const d = new Date(dateIso)
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function daysInMonth(year: number, month0: number) {
  return new Date(year, month0 + 1, 0).getDate()
}

function monthNameShort(month0: number) {
  return new Date(2000, month0, 1).toLocaleString(undefined, { month: "short" })
}

function strongestFeelingOfDay(entry?: DailyEntry): FeelingEntry | undefined {
  if (!entry?.feelings?.length) return undefined
  // strongest = max strength; tie-breaker = higher feelingId
  return entry.feelings.reduce((best, cur) => {
    if (!best) return cur
    if (cur.strength > best.strength) return cur
    if (cur.strength === best.strength && cur.feelingId > best.feelingId) return cur
    return best
  }, entry.feelings[0])
}

function addDays(d: Date, n: number) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  x.setDate(x.getDate() + n)
  return x
}

function getFixedWeekStartsForMonth(year: number, month0: number): Date[] {
  // Fixed 7-day blocks anchored at day 1: 1, 8, 15, 22, 29 (if exists)
  const dim = new Date(year, month0 + 1, 0).getDate()
  const starts: Date[] = []
  for (let day = 1; day <= dim; day += 7) {
    starts.push(new Date(year, month0, day))
  }
  return starts // length 4 or 5 typically
}

function startOfWeekMonday(d: Date) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const dow = (x.getDay() + 6) % 7 // Mon=0..Sun=6
  x.setDate(x.getDate() - dow)
  return x
}

function endOfWeekSunday(d: Date) {
  return addDays(startOfWeekMonday(d), 6)
}

// ISO week number (week-of-year), Monday-based
function getISOWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  // Thursday in current week decides the year
  const day = d.getUTCDay() || 7 // Sun=0 -> 7
  d.setUTCDate(d.getUTCDate() + 4 - day)
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
  const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
  return weekNo
}

function formatYMD(d: Date) {
  return isoDayFromDate(d) // you already have isoDayFromDate => YYYY-MM-DD
}

function formatDMY(d: Date) {
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`
}

export {
  isoDayFromDate,
  startOfDayLocal,
  daysInMonth,
  monthNameShort,
  strongestFeelingOfDay,
  addDays,
  getFixedWeekStartsForMonth,
  startOfWeekMonday,
  endOfWeekSunday,
  getISOWeekNumber,
  formatYMD,
  formatDMY,
}
